package com.iregained.eventapp.components.sections.header.sections

import androidx.compose.runtime.*
import com.iregained.eventapp.components.sections.header.styles.HeaderButtonStyle
import com.iregained.eventapp.components.sections.header.styles.NavHeaderStyle
import com.iregained.eventapp.components.sections.header.styles.SideMenuSlideInAnim
import com.iregained.eventapp.theme.toSitePalette
import com.iregained.eventapp.utils.LocalBreakpoint
import com.iregained.eventapp.utils.Res
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.icons.CloseIcon
import com.varabyte.kobweb.silk.components.icons.HamburgerIcon
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.overlay.Overlay
import com.varabyte.kobweb.silk.components.overlay.OverlayVars
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.style.breakpoint.displayUntil
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*

enum class SideMenuState {
    CLOSED, OPEN, CLOSING;

    fun close() = when (this) {
        CLOSED -> CLOSED
        OPEN -> CLOSING
        CLOSING -> CLOSING
    }
}

@Composable
private fun HamburgerButton(onClick: () -> Unit) {
    val palette = ColorMode.current.toSitePalette()
    Box(
        modifier = Modifier.padding(8.px).cursor(Cursor.Pointer).color(palette.custom.darkText).onClick {
            it.preventDefault()
            it.stopPropagation()
            onClick()
        }, contentAlignment = Alignment.Center
    ) { HamburgerIcon(modifier = Modifier.size(28.px)) }
}

@Composable
private fun CloseButton(onClick: () -> Unit) {
    val palette = ColorMode.current.toSitePalette()
    Box(
        modifier = Modifier.padding(8.px).cursor(Cursor.Pointer).color(palette.custom.darkText).onClick {
            it.preventDefault()
            it.stopPropagation()
            onClick()
        }, contentAlignment = Alignment.Center
    ) { CloseIcon(modifier = Modifier.size(28.px)) }
}

@Composable
fun NavHeader() {
    val palette = ColorMode.current.toSitePalette()
    val breakpoint = LocalBreakpoint.current
    var menuState by remember { mutableStateOf(SideMenuState.CLOSED) }

    LaunchedEffect(breakpoint) { if (breakpoint >= Res.Breakpoints.MD) menuState = SideMenuState.CLOSED }

    Row(NavHeaderStyle.toModifier(), verticalAlignment = Alignment.CenterVertically) {
        Link("/") {
            Image("/ir-logo.svg", "IRegained Inc.", Modifier.height(36.px).display(DisplayStyle.Block))
        }
        Spacer()

        // Desktop Layout: Only the Learn More button
        Row(Modifier.gap(8.px).displayIfAtLeast(Breakpoint.MD), verticalAlignment = Alignment.CenterVertically) {
            Button(
                onClick = {
                    window.open("https://iregained.rehab", "_blank")
                },
                modifier = HeaderButtonStyle.toModifier().backgroundColor(palette.custom.brandPrimary)
                    .borderRadius(8.px).padding(leftRight = 16.px, topBottom = 8.px)
            ) { SpanText("Learn More", modifier = Modifier.color(Colors.White).fontWeight(FontWeight.Bold)) }
        }

        // Mobile Layout: Hamburger Menu
        Row(Modifier.displayUntil(Breakpoint.MD), verticalAlignment = Alignment.CenterVertically) {
            HamburgerButton(onClick = { menuState = SideMenuState.OPEN })
        }
    }

    if (menuState != SideMenuState.CLOSED) {
        SideMenu(
            menuState = menuState,
            close = { menuState = menuState.close() },
            onAnimationEnd = { if (menuState == SideMenuState.CLOSING) menuState = SideMenuState.CLOSED })
    }
}

@Composable
private fun SideMenu(menuState: SideMenuState, close: () -> Unit, onAnimationEnd: () -> Unit) {
    Overlay(
        Modifier.setVariable(OverlayVars.BackgroundColor, rgba(0, 0, 0, 0.4)).zIndex(1000).onClick { close() }) {
        key(menuState) {
            Column(
                Modifier.fillMaxHeight().width(clamp(14.cssRem, 70.percent, 20.cssRem)).align(Alignment.CenterEnd)
                .padding(1.5.cssRem).gap(1.5.cssRem)
                .backgroundColor(ColorMode.current.toSitePalette().custom.cardBackground)
                .boxShadow((-4).px, 0.px, 20.px, color = rgba(0, 0, 0, 0.15)).animation(
                    SideMenuSlideInAnim.toAnimation(
                        duration = 200.ms,
                        timingFunction = if (menuState == SideMenuState.OPEN) AnimationTimingFunction.EaseOut else AnimationTimingFunction.EaseIn,
                        direction = if (menuState == SideMenuState.OPEN) AnimationDirection.Normal else AnimationDirection.Reverse,
                        fillMode = AnimationFillMode.Forwards
                    )
                ).borderRadius(topLeft = 16.px, bottomLeft = 16.px).onClick { it.stopPropagation() }
                .onAnimationEnd { onAnimationEnd() }, horizontalAlignment = Alignment.End
            ) {
                CloseButton(onClick = { close() })
                Column(Modifier.fillMaxWidth().gap(1.cssRem), horizontalAlignment = Alignment.Start) {

                    // Mobile Menu: Only the Learn More button
                    Button(
                        onClick = {
                            window.open("https://iregained.rehab", "_blank")
                            close()
                        },
                        modifier = HeaderButtonStyle.toModifier().fillMaxWidth().margin(top = 16.px)
                            .backgroundColor(ColorMode.current.toSitePalette().custom.brandPrimary).borderRadius(8.px)
                            .padding(topBottom = 12.px)
                    ) { SpanText("Learn More", modifier = Modifier.color(Colors.White).fontWeight(FontWeight.Bold)) }
                }
            }
        }
    }
}